import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { SECTION_LOGIN } from './constants';
import { trackRegistration } from './util';
import { resetPassword, emailReset, validateResetKey } from './api';
import AccountContext from '../../services/AccountContext';
import { FloatingLabelInput, Button } from 'edcom-ui';

const FORGOT_VIEW = 'forgot';
const CONFIRM_VIEW = 'confirm';
const RESET_VIEW = 'reset';
const ERROR_VIEW = 'error';

const ResetPassword = ({
  setSection,
  setIsLoading,
  changePasswordModal,
  setIsOpen,
}) => {
  const [view, setView] = useState(FORGOT_VIEW);
  const [showChangedDiag, setShowChangedDiag] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState({
    'email-address': '',
    general: '',
  });
  const [passwordError, setPasswordError] = useState({
    'email-verification': '',
    password: '',
    general: '',
  });
  const emailInputEl = useRef(null);
  const passwordInputEl = useRef(null);

  const { refresh } = useContext(AccountContext);

  const queryStringKey =
    (location.search.match(/[?&]key=([^&]+)/) || [])[1] || null;

  async function validateResetKeyFromQueryString() {
    // TODO: more robust regex
    if (location.search.match(/reset-password/) && queryStringKey) {
      setView(RESET_VIEW);
      try {
        const json = await validateResetKey(queryStringKey);
        if (!json.success) {
          setView(ERROR_VIEW);
        }
      } catch (e) {
        setEmailError(
          Object.assign({}, emailError, { general: 'An error occurred.' })
        );
        setView(FORGOT_VIEW);
      }
    }
  }

  useEffect(() => {
    validateResetKeyFromQueryString();
  }, []);

  const resetErrors = () => {
    setEmailError({ 'email-address': '', general: '' });
    setPasswordError({ 'email-verification': '', password: '', general: '' });
  };

  const validateEmailPresence = () => {
    resetErrors();
    if (email.length === 0) {
      setEmailError(
        Object.assign({}, emailError, {
          'email-address': 'Please enter your email address.',
        })
      );
      return false;
    }
    return true;
  };

  const validatePasswordPresence = () => {
    resetErrors();
    if (password.length === 0) {
      setPasswordError(
        Object.assign({}, passwordError, {
          password: 'Please enter your password.',
        })
      );
      return false;
    }
    return true;
  };

  const confirmEmailSentOrSetErrors = data => {
    if (data.success) {
      trackRegistration(
        'reset-password email sent',
        'Requested Password Reset'
      );
      resetErrors();
      setEmail('');
      setView(CONFIRM_VIEW);
    } else if (data.error_type === 'email') {
      setEmailError(
        Object.assign({}, emailError, {
          'email-address': data.msg,
          general: 'Please fix the errors above in red and try again.',
        })
      );
    } else if (data.error_type === 'general') {
      setEmailError(Object.assign({}, emailError, { general: data.msg }));
    } else if (!data.status) {
      setEmailError({ general: 'Unexpected Error', 'email-address': '' });
    } else {
      setEmailError(
        Object.assign({}, emailError, { general: 'There was an error.' })
      );
    }
    setIsLoading(false);
  };

  const submitEmailForReset = async e => {
    e.preventDefault();
    emailInputEl.current.focus();
    if (!validateEmailPresence(email)) return;

    setIsLoading(true);
    try {
      const response = await emailReset(email);
      confirmEmailSentOrSetErrors(response);
    } catch {
      confirmEmailSentOrSetErrors({ status: 0 });
    }
  };

  const refreshOnSuccessOrSetErrors = data => {
    if (data.success) {
      trackRegistration('reset-password successful', 'Reset Password');

      refresh()
        .then(() => {
          setIsLoading(false);
          setIsOpen(false);
        })
        .catch(() => {
          // Some issue refreshing account state client-side, do a full reload instead
          window.location.reload();
        });
      return;
    } else if (data.status === 0) {
      setPasswordError({ general: 'Unexpected Error', 'email-address': '' });
    } else if (data.error_type === 'key') {
      setPasswordError(
        Object.assign({}, passwordError, { 'email-verification': data.msg })
      );
    } else if (data.error_type === 'password') {
      setPasswordError(
        Object.assign({}, passwordError, { password: data.msg })
      );
    } else {
      setPasswordError(
        Object.assign({}, passwordError, { general: 'There was an error.' })
      );
    }

    setIsLoading(false);
  };

  const submitPasswordChange = async e => {
    e.preventDefault();
    passwordInputEl.current.focus();

    if (!validatePasswordPresence()) return;

    setIsLoading(true);

    try {
      const key = (location.search.match(/[?&]key=([^&]+)/) || [])[1];
      const response = await resetPassword(key, password);
      refreshOnSuccessOrSetErrors(response);
    } catch {
      refreshOnSuccessOrSetErrors({ status: 0 });
    }
  };

  // RENDER
  if (view === FORGOT_VIEW) {
    return (
      <div className="forgotPassword">
        <h3>Forgot your password?</h3>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        <p>
          Please enter your email address and we&apos;ll send you instructions
          to reset your password.
        </p>
        <form className="form registration-form" onSubmit={submitEmailForReset}>
          <div
            className={clsx(
              'input-row email-address ',
              emailError['email-address'] && 'has-error'
            )}
          >
            <FloatingLabelInput
              label={'Email address'}
              type="email"
              onChange={e => {
                setEmail(e.target.value);
              }}
              onBlur={e => {
                setEmail(e.target.value);
              }}
              ref={emailInputEl}
              autoFocus={true}
              required={true}
            />
            <div className="errormsg">{emailError['email-address']}</div>
            <a
              className="email-changed"
              onClick={() => setShowChangedDiag(true)}
            >
              Has your email changed?
            </a>
          </div>

          {showChangedDiag && (
            <div className="email-changed-text">
              <p>
                {' '}
                If you no longer have access to the email address associated
                with your account, contact{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://support.education.com"
                >
                  Customer Service
                </a>{' '}
                for help restoring access to your account.
              </p>
            </div>
          )}

          <div className="buttons">
            <Button
              color="tertiary"
              onClick={() => {
                if (changePasswordModal) {
                  setIsOpen(false);
                } else {
                  trackRegistration(
                    'opened',
                    'Viewed Registration Form',
                    'login'
                  );
                  setSection(SECTION_LOGIN);
                }
              }}
            >
              Cancel
            </Button>
            <Button size="normal" type="submit">
              Send Email
            </Button>
          </div>

          <div
            className={clsx(
              'input-row general ',
              emailError.general && 'has-error'
            )}
          >
            <div className="errormsg">{emailError.general}</div>
          </div>
        </form>
      </div>
    );
  } else if (view === CONFIRM_VIEW) {
    return (
      <div className="confirm-email">
        <h3>Reset Password Email Sent</h3>
        <p className="subhead">
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          The email is on its way. Please allow a few minutes for it to arrive.
          Didn&apos;t receive the email?
          <a
            className="forgot"
            onClick={() => {
              trackRegistration(
                'Change Password',
                'forgot password click',
                'Clicked Forgot Password Link'
              );
              setView(FORGOT_VIEW);
            }}
          >
            {' '}
            Go back and try again
          </a>
        </p>
        <p className="description" style={{ margin: '10px 0' }} />
      </div>
    );
  } else if (view === RESET_VIEW) {
    return (
      <div className="reset-password">
        <div>
          <h3>Set/Reset Password</h3>
        </div>
        <div className="clear" />
        <form
          className="form registration-form"
          onSubmit={submitPasswordChange}
        >
          <div
            className={clsx(
              'input-row password ',
              passwordError.password && 'has-error'
            )}
          >
            <FloatingLabelInput
              label="New Password"
              type="password"
              autoComplete="off"
              onChange={e => {
                setPassword(e.target.value);
              }}
              ref={passwordInputEl}
              autoFocus
              required
            />
            <div className="errormsg">{passwordError.password}</div>
          </div>
          <div
            className={clsx(
              'input-row email-verification ',
              passwordError['email-verification'] && 'has-error'
            )}
          >
            <div className="errormsg">
              {passwordError['email-verification']}
            </div>
          </div>
          <Button type="submit">Change Password and Login</Button>
          <div
            className={clsx(
              'input-row general ',
              passwordError.general && 'has-error'
            )}
          >
            <div className="errormsg">{passwordError.general}</div>
          </div>
        </form>
      </div>
    );
  } else if (view === ERROR_VIEW) {
    return (
      <div className="error-message" style={{ display: 'block' }}>
        <h3>Set/Reset Password</h3>
        <div style={{ 'margin-top': '10px' }}>
          The key is expired or invalid. Please reset the password again.
        </div>
        <div
          style={{
            'text-align': 'center',
            display: 'block',
            padding: '20px 0 10px 0',
          }}
        >
          <a
            className="forgot-below forgot"
            onClick={() => {
              setView(FORGOT_VIEW);
            }}
          >
            Reset Password{' '}
          </a>
        </div>
      </div>
    );
  }
  return null;
};
ResetPassword.propTypes = {
  setSection: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  changePasswordModal: PropTypes.bool,
};
ResetPassword.defaultProps = {
  changePasswordModal: false,
};
export default ResetPassword;
